import { Field } from '@sitecore-jss/sitecore-jss-vue';
import { parse, format, startOfDay, isValid, parseISO } from 'date-fns';

import { INameCode } from '@/interfaces/entities/name-code.interface';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';

/**
 * Convert an Array of objects into an Object.
 *
 * @param arr The Array to map over.
 * @param key The desired field for each Object key.
 *            If no key is passe in, it is assumed that this will be used
 *            for sitecore form inputs using the formInputId.value
 *
 */
export function convertArrayToObject(arr: Record<any, any>[], key?: string): Record<string, any> {
  if (!arr?.length) return {};

  const newObject = Object.assign(
    {},
    ...arr
      // shouldn't have to do this ... it means a field is broken in sitecore
      .filter((item) => (item?.formInputId?.value ? true : false))
      //
      .map((item) => ({
        [key || item.formInputId.value]: item,
      }))
  );

  return newObject;
}

/**
 * Convert a PascalCase sting to kebab-case
 *
 * Used for converting Vue component names
 * to kebab case for file path importing.
 *
 */
export function pascalCaseToKebabCase(str = '') {
  return str
    .match(/[A-Z][0-9a-z]*/g)
    ?.join('-')
    .toLowerCase();
}

/**
 * Restrict decimals to a set number of digits.
 *
 * @param num
 * @param digits
 * @returns
 */
export function truncateDecimals(num: number, digits = 2) {
  const multiplier = Math.pow(10, digits);
  const adjustedNum = num * multiplier;
  const truncatedNum = Math[adjustedNum < 0 ? 'ceil' : 'floor'](adjustedNum);

  return truncatedNum / multiplier;
}

// TODO: Deprecated: This is a bad pattern, do not use. Instead see Homecontents_CoverDetails.vue for an example of mapping dynamic options
export function convertReferenceDataNameCodeToDynamicOptions(apiResponse: INameCode[], excludeCode?: string): any {
  if (!apiResponse || !apiResponse.values) return '';

  let filteredResponse;
  if (Array.isArray(apiResponse.values)) {
    filteredResponse = JSON.parse(JSON.stringify(apiResponse.values));
  } else {
    filteredResponse = JSON.parse(JSON.stringify(apiResponse));
  }

  if (filteredResponse && excludeCode) {
    filteredResponse = filteredResponse.filter((item: any) => item.code !== excludeCode);
  }

  return filteredResponse
    .map((item: any) => `${item.name}=${item.code}`)
    .map((e: any) => e)
    .join('&');
}

// TODO: Deprecated: This is a bad pattern, do not use. Instead see Homecontents_CoverDetails.vue for an example of mapping dynamic options
export function convertReferenceDataValueCodeToDynamicOptions(apiResponse: INameValue[], excludeCode?: string): any {
  if (!apiResponse) return '';

  let filteredResponse;
  if (Array.isArray(apiResponse.values)) {
    filteredResponse = JSON.parse(JSON.stringify(apiResponse.values));
  } else {
    filteredResponse = JSON.parse(JSON.stringify(apiResponse));
  }

  if (filteredResponse && excludeCode) {
    filteredResponse = filteredResponse.filter((item: any) => item.value !== excludeCode);
  }

  return filteredResponse
    .map((item: any) => `${item.name}=${item.value}`)
    .map((e: any) => e)
    .join('&');
}

export const convertToISOString = (dateString: string): string => {
  const [day, month, year] = dateString.split('/').map(Number);
  const dateObj = new Date(Date.UTC(year, month - 1, day)); // Months are 0-indexed in JavaScript
  dateObj.setMilliseconds(0);

  return dateObj.toISOString().replace('.000Z', 'Z');
};

export const convertToISODate = (dateString: string): Date => {
  const [day, month, year] = dateString.split('/').map(Number);
  const dateObj = new Date(Date.UTC(year, month - 1, day)); // Months are 0-indexed in JavaScript
  dateObj.setMilliseconds(0);

  return dateObj;
};

export const convertISOToDate = (isoString: string): string => {
  const dateObj = new Date(isoString);
  return convertISOToDateFormatted(dateObj);
};

export const convertISOToDateFormatted = (date: Date): string => {
  // Using Brisbane timezone to make sure everybody will get the same date
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Australia/Brisbane',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  return new Intl.DateTimeFormat('en-GB', options).format(date);
};

// Function to transform a generic field to the expected structure
export const transformField = (field: Field) => {
  return {
    value: typeof field.value === 'string' || typeof field.value === 'number' ? field.value : undefined,
    editable: field.editable,
  };
};

/**
 * Function to map form step to index of an array in the quote and return the current quote index for the step.
 * For example useful when needing to know which Pet to load and which Pet item to remove from the quote.
 * @param formStore
 * @param purchaseStepId
 * @param currentStepIndex
 * @returns
 */
export const getQuoteArrayItemIndexByStepIndex = (formStore: any, purchaseStepId: string, currentStepIndex: number) => {
  if (!formStore || !purchaseStepId || !currentStepIndex) return -1;

  const mapStepIndexAndIndexMapping = formStore.inScopeSteps
    .filter((x: any) => x.id.toLowerCase() === purchaseStepId.toLocaleLowerCase())
    .map((x: any, i: number) => {
      return { stepIndex: x.stepIndex, index: i };
    });

  const currentStep = mapStepIndexAndIndexMapping.find((x: any) => x.stepIndex === currentStepIndex);
  return currentStep ? currentStep?.index : -1;
};

/**
 * Convert Date to dateString string
 *
 * @param date
 * @param outputStringFormat
 */
export function dateToDateString(inputDate: Date, outputStringFormat: 'dd/MM/yyyy' | 'ddMMyyyy' | 'yyyy-MM-dd') {
  return format(inputDate, outputStringFormat);
}

/**
 * Convert dateDtring to Date
 *
 * @param inputDateString
 * @param inputStringFormat
 */
export function dateStringToDate(inputDateString: string, inputStringFormat: 'dd/MM/yyyy' | 'ddMMyyyy' | 'yyyy-MM-dd') {
  return parse(inputDateString, inputStringFormat, new Date());
}

/**
 * Convert date to ISO string
 * @param inputDate
 * @returns {string} ISO string
 */
export function getStartOfISODate(inputDate: Date = new Date()) {
  return startOfDay(inputDate).toISOString();
}

/**
 * Flat map fields, { field1: { value: 123 } } => { field1: 123 }
 * @param item
 * @returns
 */
export function flatMapFields(item: Record<string, { value: any }>): Record<string, any> {
  const keys = Object.keys(item);
  const result: Record<string, any> = {};
  keys.forEach((key: string) => (result[key] = item[key]?.value));
  return result;
}

/**
 * Trim html to single line text
 * @param input
 * @returns
 */
export function trimHTML(input: string) {
  try {
    return input.replace(/<\/?[^>]+(>|$)/g, '');
  } catch {
    return input;
  }
}

export function getPeriodOfInsurance(termStartDate?: Date): string {
  if (!termStartDate) return '';

  const startDate = new Date(termStartDate);
  // Check if the date is valid.
  if (isNaN(startDate.getTime())) return '';

  const endDate = new Date(startDate);
  // Add one year to the start date.
  endDate.setFullYear(startDate.getFullYear() + 1);
  // Set time to 11:59 PM.
  endDate.setHours(23, 59, 0, 0);

  return `${format(startDate, STANDARD_DATE_FORMAT)} - ${format(endDate, STANDARD_DATE_FORMAT)}`;
}

type Key = string | number | symbol;

export function filterObject<T extends Record<Key, unknown>, K extends keyof T>(
  obj: T,
  keys?: readonly K[]
): Pick<T, K> {
  const ks = (keys ?? []) as readonly K[]; // <-- avoids never[]
  return Object.fromEntries(ks.filter((k): k is K => k in obj).map((k) => [k, obj[k]])) as Pick<T, K>;
}

export const keepOnlyKeys = <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  return keys.reduce(
    (acc, key) => {
      acc[key] = obj[key];
      return acc;
    },
    {} as Pick<T, K>
  );
};

export function formatDateValue(input: unknown): string {
  if (input instanceof Date && isValid(input)) return format(input, STANDARD_DATE_FORMAT);

  if (typeof input === 'string') {
    const parsed = parseISO(input);
    if (isValid(parsed)) return format(parsed, STANDARD_DATE_FORMAT);

    const asNum = Number(input);
    if (!Number.isNaN(asNum)) {
      const d = new Date(asNum);
      if (isValid(d)) return format(d, STANDARD_DATE_FORMAT);
    }
  }

  if (typeof input === 'number') {
    const d = new Date(input);
    if (isValid(d)) return format(d, STANDARD_DATE_FORMAT);
  }

  // Fallback (don’t break—just return original as string)
  return String(input ?? '');
}
